import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Header from "../../Header";
import { ConstructorEventUpdateSQLStatus } from "../../../Event/event";

const BreakAndAdd = () => {
  const [num1, setNum1] = useState(0);
  const [num2, setNum2] = useState(0);
  const [total, setTotal] = useState(0);
  const [tens1, setTens1] = useState(0);
  const [ones1, setOnes1] = useState(0);
  const [tens2, setTens2] = useState(0);
  const [ones2, setOnes2] = useState(0);
  const [step, setStep] = useState("");
  const [blocks, setBlocks] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [feedback, setFeedback] = useState("");
  const [inputEnabled, setInputEnabled] = useState(false);
  const [showNext, setShowNext] = useState(false);

  const styles = {
    body: {
      fontFamily: `'Comic Neue', cursive, sans-serif`,
      background: "#fffbe6",
      color: "#3e2723",
      padding: "2rem 1rem",
      minHeight: "100vh",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      boxSizing: "border-box",
    },
    title: {
      fontSize: "2rem",
      marginBottom: "1rem",
      color: "#5d4037",
      textAlign: "center",
    },
    problem: {
      fontSize: "2.2rem",
      marginBottom: "1.2rem",
      fontWeight: 700,
    },
    animationArea: {
      marginBottom: "2rem",
      minHeight: "200px",
      maxWidth: "500px",
      width: "100%",
      display: "flex",
      flexWrap: "wrap",
      justifyContent: "center",
    },
    input: {
      fontSize: "1.5rem",
      padding: "0.5rem 1rem",
      borderRadius: "10px",
      border: "3px solid #5d4037",
      textAlign: "center",
      width: "100%",
      maxWidth: "180px",
      margin: "0 auto 1rem",
      display: "block",
    },
    button: {
      padding: "0.75rem 1.5rem",
      fontSize: "1rem",
      fontWeight: 700,
      color: "white",
      backgroundColor: "#6d4c41",
      border: "none",
      borderRadius: "25px",
      cursor: "pointer",
      margin: "0.5rem auto",
      display: "block",
      width: "fit-content",
      boxShadow: "0 6px 18px rgba(109, 76, 65, 0.4)",
    },
    nextButton: {
      padding: "0.75rem 1.5rem",
      fontSize: "1rem",
      fontWeight: 700,
      color: "white",
      backgroundColor: "#fb8c00",
      border: "none",
      borderRadius: "25px",
      cursor: "pointer",
      margin: "1rem auto 0",
      display: "block",
      width: "fit-content",
      boxShadow: "0 6px 18px rgba(251, 140, 0, 0.6)",
    },
    feedback: {
      fontSize: "1.2rem",
      fontWeight: "700",
      minHeight: "40px",
      color: "#4e342e",
      textAlign: "center",
      marginTop: "1.2rem",
    },
  };

  const delay = (ms) => new Promise((res) => setTimeout(res, ms));
  const randomNumber = () => Math.floor(Math.random() * 90) + 10;

  const createBlocks = (value, type, group) => {
    return Array.from({ length: value }, (_, i) => (
      <motion.div
        key={`${type}-${group}-${i}-${Math.random()}`}
        initial={{ scale: 0, opacity: 0, y: -50 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0 }}
        transition={{ type: "spring", stiffness: 200, damping: 12 }}
        style={{
          width: "45px",
          height: "45px",
          margin: "6px",
          borderRadius: "10px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          fontSize: "1.2rem",
          fontWeight: "bold",
          color: "white",
          backgroundColor: type === "tens" ? "#ff7043" : "#8d6e63",
          boxShadow: "0 3px 8px rgba(0,0,0,0.2)",
        }}
      >
        {type === "tens" ? "10" : "1"}
      </motion.div>
    ));
  };

  const animateAddition = async () => {
    const n1 = randomNumber();
    const n2 = randomNumber();
    const t1 = Math.floor(n1 / 10);
    const o1 = n1 % 10;
    const t2 = Math.floor(n2 / 10);
    const o2 = n2 % 10;
    const sum = n1 + n2;

    setNum1(n1);
    setNum2(n2);
    setTens1(t1);
    setOnes1(o1);
    setTens2(t2);
    setOnes2(o2);
    setTotal(sum);
    setInputEnabled(false);
    setInputValue("");
    setFeedback("");
    setShowNext(false);
    setBlocks([]);
    setStep("");

    await delay(500);
    setStep(`Break ${n1} into: ${t1} tens and ${o1} ones`);
    setBlocks(createBlocks(t1, "tens", "first"));
    await delay(1000);
    setBlocks((prev) => [...prev, ...createBlocks(o1, "ones", "first")]);

    await delay(1200);
    setStep(`Break ${n2} into: ${t2} tens and ${o2} ones`);
    setBlocks((prev) => [...prev, ...createBlocks(t2, "tens", "second")]);
    await delay(1000);
    setBlocks((prev) => [...prev, ...createBlocks(o2, "ones", "second")]);

    await delay(1200);
    setStep(`Add tens: ${t1 * 10} + ${t2 * 10} = ${(t1 + t2) * 10}`);
    setBlocks([...createBlocks(t1 + t2, "tens", "sumTens")]);

    await delay(1500);
    setStep(`Add ones: ${o1} + ${o2} = ${o1 + o2}`);
    setBlocks((prev) => [...prev, ...createBlocks(o1 + o2, "ones", "sumOnes")]);

    await delay(1500);
    setStep(`✨ Total = ${(t1 + t2) * 10} + ${o1 + o2} = ${sum}`);
    setInputEnabled(true);
  };

  useEffect(() => {
    animateAddition();
  }, []);

  const handleSubmit = () => {
    const answer = parseInt(inputValue, 10);
    if (answer === total) {
      setFeedback("🎉 Correct! Great job breaking and adding.");
      setInputEnabled(false);
      const responsedata = ConstructorEventUpdateSQLStatus();
      console.log("Event completed", responsedata);
      setShowNext(true);
    } else {
      setFeedback("❌ Not quite right. Try again!");
      setInputValue("");
    }
  };

  return (
    <div style={styles.body}>
      {/* <Header /> */}
      <h1 style={styles.title}>Break & Add</h1>
      <div style={styles.problem}>{`${num1} + ${num2}`}</div>

      <div style={styles.animationArea}>
        <AnimatePresence>{blocks}</AnimatePresence>
      </div>

      <input
        type="number"
        inputMode="numeric"
        placeholder="Total answer"
        aria-label="Type the total sum here"
        style={styles.input}
        disabled={!inputEnabled}
        value={inputValue}
        onChange={(e) => {
          setInputValue(e.target.value);
          setFeedback("");
        }}
      />

      <button style={styles.nextButton} onClick={handleSubmit} disabled={!inputEnabled}>
        Submit
      </button>

      {showNext && (
        <button style={styles.button} onClick={animateAddition}>
          Next
        </button>
      )}

      <div style={styles.feedback}>{feedback}</div>
    </div>
  );
};

export default BreakAndAdd;
